import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { PageHero } from "../components/site/PageHero";
import { Reveal, SectionHead, spotMove } from "../components/site/Motion";
import { CtaBand } from "../components/home/CtaBand";
import { useRfq } from "../components/site/RfqContext";
import { Seo, crumbsLd } from "../components/site/Seo";
import { fileUrl, useCategories } from "../lib/api";
import { INDUSTRIES } from "../lib/industries";

const IndustryCard = ({ ind, i }) => {
  const { open } = useRfq();
  const Icon = ind.icon;
  return (
    <Reveal delay={(i % 3) * 0.08} className="h-full">
      <article
        onMouseMove={spotMove}
        data-testid={`industry-card-${ind.key}`}
        className="spot clip-corner group relative flex h-full min-h-[440px] flex-col justify-end overflow-hidden bg-[#07090e]"
      >
        <img src={ind.img} alt={`Rubber parts for ${ind.name}`} loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-75 transition-[transform,opacity] duration-[1.2s] group-hover:scale-[1.06] group-hover:opacity-90" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#07090e] via-[#07090e]/70 to-transparent" />
        <div className="absolute left-6 right-6 top-6 z-10 flex items-start justify-between">
          <span className="flex h-12 w-12 items-center justify-center bg-white text-[#0a6cff] transition-[background-color,color] duration-300 group-hover:bg-[#0a6cff] group-hover:text-white">
            <Icon className="h-6 w-6" strokeWidth={1.6} />
          </span>
          <span className="font-mono-x text-xs text-slate-300">{String(i + 1).padStart(2, "0")} / 06</span>
        </div>
        <div className="relative z-10 p-6">
          <h2 className="font-display text-4xl font-extrabold uppercase leading-[0.9] text-white">{ind.name}</h2>
          <p className="mt-3 text-sm leading-relaxed text-slate-300">{ind.desc}</p>
          <ul className="mt-4 flex flex-wrap gap-1.5">
            {ind.parts.map((p) => (
              <li key={p} className="border border-white/20 bg-white/5 px-2.5 py-1 text-[11px] font-medium uppercase tracking-wider text-slate-200 backdrop-blur">{p}</li>
            ))}
          </ul>
          <button data-testid={`industry-rfq-${ind.key}`} onClick={() => open(`Parts for ${ind.name}`)} className="mt-5 inline-flex items-center gap-2 text-left text-xs font-semibold uppercase tracking-widest text-sky-400 transition-colors hover:text-sky-300">
            Get a quote for {ind.name} <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </article>
    </Reveal>
  );
};

const ProductStrip = () => {
  const { categories } = useCategories();
  if (!categories.length) return null;
  const loop = [...categories, ...categories];
  return (
    <section data-testid="industries-products-strip" className="overflow-hidden bg-slate-50 py-24 md:py-32">
      <div className="container-x mb-12 flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <SectionHead eyebrow="Our products" title="One partner, every part." />
        <Link to="/products" data-testid="industries-view-products" className="btn-electric self-start md:self-end">View catalogue <ArrowRight className="h-4 w-4" /></Link>
      </div>
      <div className="marquee-wrap relative">
        <div className="marquee-track flex w-max gap-5 pl-5" style={{ animationDuration: "60s" }}>
          {loop.map((c, i) => (
            <Link key={i} to={`/products/${c.slug}`} data-testid={`industries-product-${c.slug}-${i}`} className="group w-[260px] shrink-0 md:w-[320px]">
              <div className="clip-corner aspect-square overflow-hidden bg-[#07090e]">
                <img src={fileUrl(c.image)} alt={c.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.07]" />
              </div>
              <p className="mt-4 flex items-center justify-between font-display text-2xl font-extrabold uppercase text-slate-950">
                {c.name}<ArrowUpRight className="h-5 w-5 text-slate-400 transition-[color,transform] group-hover:rotate-45 group-hover:text-[#0a6cff]" />
              </p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default function Industries() {
  return (
    <div data-testid="industries-page">
      <Seo
        path="/industries"
        title="Industries We Serve: Automobile, Electrical & More | Autocrat"
        description="Autocrat Rubber makes rubber parts for automobile, electrical, oil & gas, home appliance, medical equipment and plumbing & bathware fittings across India."
        jsonLd={crumbsLd([["Home", "/"], ["Industries", "/industries"]])}
      />
      <PageHero testId="industries-hero" video eyebrow="Industries we serve" lines={["Industries", { text: "we serve.", className: "text-outline" }]} crumbs={[{ to: "/", label: "Home" }, { label: "Industries" }]}>
        From vehicles and power systems to kitchens, clinics and bathrooms — Autocrat rubber parts keep products sealed, insulated and running across six core industries.
      </PageHero>
      <section className="bg-white py-24 md:py-32">
        <div className="container-x">
          <SectionHead eyebrow="Six sectors · one standard" title={<>Built for the job <span className="text-[#0a6cff]">it has to do.</span></>}>
            Every industry asks something different of rubber — oil resistance, heat, hygiene, insulation or long life outdoors. We match the compound and design to the application.
          </SectionHead>
          <div data-testid="industries-grid" className="mt-14 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {INDUSTRIES.map((ind, i) => <IndustryCard key={ind.key} ind={ind} i={i} />)}
          </div>
        </div>
      </section>
      <ProductStrip />
      <CtaBand />
    </div>
  );
}
